import { useRef } from 'react'

import { useUpdateIncidentRules } from '../hooks/useUpdateIncidentRules'
import { UseFormMethods } from 'react-hook-form'

import { Box, Flex, ScaleFade } from '@chakra-ui/react'

import {
  FormDatePickerControl,
  FormInputSelectControl,
  FormRadioInputControl,
  FormTextareaControl,
} from '@/components/ui'
import { Action, RuleEventType } from '@/graphql/generated/schemas'
import { OptionI } from '@/utils/forms/optionBuilders'

import { AiOperatorCriteria } from '../ai-operator-criteria/components/AiOperatorCriteria'
import { DryRunToggle } from '../ai-operator-criteria/components/DryRunToggle'
import { convertResponseRulesToPreviewData } from '../ai-operator-criteria/utils/utils'
import { FormInputsT, RuleInputDataIProps } from '../types/types'
import { MAX_RULENAME_LENGTH, actionOptions } from '../utils/utils'
import { CrossDayAlert } from './CrossDayAlert'
import { EmailInputSelectControl } from './EmailInputSelectControl'
import { LabeledSwitch } from './LabeledSwitch'
import { RecurrenceToggle } from './RecurrenceToggle'
import { RuleScheduleAlert } from './RuleScheduleAlert'
import { RuleSection } from './RuleSection'

export interface IncidentRuleViewIProps {
  rulesData: RuleInputDataIProps
  form: UseFormMethods<FormInputsT>
}

export const IncidentRuleView = ({
  rulesData,
  form,
}: IncidentRuleViewIProps) => {
  const deviceRef = useRef<HTMLDivElement>(null)
  const {
    days,
    doorIds,
    emailOptions,
    endDate,
    endTimeDate,
    facilityName,
    repeatIntervalNum,
    shouldRepeat,
    shouldShowDays,
    shouldShowRecipients,
    shouldEnd,
    startTime,
    timeZone,
    isLoading,
    isDeviceLoading,
    isRecipientsLoading,
    aiOperatorResponseRules,
    allDayOverride: isAllDay,
    isDryRun: isDryRunInitial,
  } = rulesData
  const { control, register, watch } = form
  const watchAction = watch('action')
  const watchEvents = watch('event')

  const isAlarmEventSelected = watchEvents?.some(
    (ev: OptionI<RuleEventType>) => ev.value === RuleEventType.Alarm
  )

  const shouldShowAlarms = isAlarmEventSelected

  const {
    isDryRun,
    onDryRunToggle,
    isLoading: isDryRunUpdateLoading,
  } = useUpdateIncidentRules({
    isDryRunInitial,
    isDryRunInitialLoading: isLoading,
    ruleId: rulesData?.id,
  })

  const isAiOperatorActionSelected =
    watchAction?.value === Action.EnableAiOperator

  const previewCriteria = isAiOperatorActionSelected
    ? convertResponseRulesToPreviewData(aiOperatorResponseRules)
    : []

  return (
    <Flex direction='column' h='calc(100vh - 85px)'>
      <Flex
        direction='row'
        gap={{ base: '2px', md: '2px', lg: '6px' }}
        h='100%'
        overflowY='scroll'
        py='20px'
        w='full'
      >
        {/* Column 1: Name, Facilities, Floors, Devices */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '0.9' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection mb={6}>
            <FormTextareaControl
              id='name'
              isDisabled={true}
              label='Name of rule'
              labelColour='#1A202C'
              labelFontWeight='600'
              labelOpacity={1}
              maxCharCount={MAX_RULENAME_LENGTH}
              placeholder='Enter a name for this rule'
              textareaRef={register({
                maxLength: {
                  value: MAX_RULENAME_LENGTH,
                  message: `Rule name should have less than ${MAX_RULENAME_LENGTH} characters`,
                },
                required: 'Rule name is required',
              })}
            />
          </RuleSection>

          <RuleSection label='Select the devices associated with the rule'>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                defaultValue={null}
                id='facility'
                isClearable
                isDisabled={true}
                isLoading={isDeviceLoading}
                isSearchable
                label='Facility'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={[]}
                rules={{ required: true }}
              />
            </Box>
            <Box mb={6}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='floor'
                isDisabled={true}
                isLoading={isDeviceLoading}
                label='Floor(s)'
                leftRadioButtonLabel='All floors'
                options={[]}
                rightRadioButtonLabel='Select one or more floors'
              />
            </Box>
            <Box ref={deviceRef}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='doors'
                isDisabled={true}
                isLoading={isDeviceLoading}
                label='Door Device(s)'
                leftRadioButtonLabel='All doors'
                options={[]}
                rightRadioButtonLabel='Select one or more doors'
              />
            </Box>
            <Box mt={3}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='cameras'
                isDisabled={true}
                isLoading={isDeviceLoading}
                label='Camera Device(s)'
                leftRadioButtonLabel='All cameras'
                options={[]}
                rightRadioButtonLabel='Select one or more cameras'
              />
            </Box>
            {shouldShowAlarms && (
              <Box mt={3}>
                <FormRadioInputControl
                  control={control}
                  dropdownContainerHeight='200px'
                  id='alarms'
                  isDisabled={true}
                  isLoading={isDeviceLoading}
                  label='Alarm Device(s)'
                  leftRadioButtonLabel='All alarms'
                  options={[]}
                  rightRadioButtonLabel='Select one or more alarms'
                />
              </Box>
            )}
          </RuleSection>
        </Flex>

        {/* Column 2: Event, Actions Schedule */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '1.1' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection label='Rule Actions' mb={6}>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                id='action'
                isClearable
                isDisabled={true}
                isLoading={isLoading}
                isSearchable
                label='Choose Action'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={actionOptions}
                rules={{ required: true }}
              />
            </Box>

            <EmailInputSelectControl
              control={control}
              defaultValue={undefined}
              id='emails'
              isDisabled
              isLoading={isRecipientsLoading}
              isOpen={shouldShowRecipients}
              label='Recipients'
              options={emailOptions}
              placeholder='Add Recipients'
            />

            {isAiOperatorActionSelected && (
              <ScaleFade
                in={true}
                initialScale={0.95}
                transition={{ enter: { duration: 0.3 } }}
              >
                <AiOperatorCriteria
                  criteria={previewCriteria}
                  criteriaOptions={previewCriteria}
                  disabled={true}
                  doorIds={doorIds}
                  onChange={() => {}}
                />
                <DryRunToggle
                  isDryRun={isDryRun}
                  isLoading={isDryRunUpdateLoading}
                  onDryRunToggle={onDryRunToggle}
                />
              </ScaleFade>
            )}
            <FormRadioInputControl
              control={control}
              id='event'
              isDisabled={true}
              label='Event(s)'
              leftRadioButtonLabel='All events'
              options={[]}
              rightRadioButtonLabel='Select one or more events'
            />
          </RuleSection>

          <RuleSection label='Select time frame for the action to be applied'>
            <RuleScheduleAlert
              facilityName={facilityName}
              timeZone={timeZone}
            />

            <CrossDayAlert endTime={endTimeDate} startTime={startTime} />

            <Box mb={6}>
              <LabeledSwitch
                id='all-day-switch'
                inputRef={null}
                isChecked={isAllDay}
                isDisabled
                label='All Day'
              />
              <FormDatePickerControl
                control={control}
                id='date'
                isAllDay={isAllDay}
                isDisabled
                selectedDate={startTime}
                selectedEndTime={endTimeDate}
                showEndTimePicker
                showStartTimePicker
                timeZone={timeZone}
              />
            </Box>

            <RecurrenceToggle
              control={control}
              days={days}
              endDate={endDate}
              isDisabled={true}
              repeatIntervalNum={repeatIntervalNum}
              selectedStartDate={startTime}
              shouldEnd={shouldEnd}
              shouldRepeat={shouldRepeat}
              shouldShowDays={shouldShowDays}
            />
          </RuleSection>
        </Flex>
      </Flex>
    </Flex>
  )
}
